/**
 * Confirm — the app's own confirmation dialog.
 *
 * `window.confirm()` is not usable on a phone. iOS Safari offers "Block Alerts"
 * after a page prompts more than once, Brave suppresses page dialogs by default,
 * and a suppressed dialog does not throw or warn: it returns `false`. Every
 * guarded action behind one therefore becomes a button that visibly does
 * nothing, which is exactly how "Return to Lobby doesn't work" was reported
 * twice while the button itself was provably present and hittable.
 *
 * This dialog is ours, so nothing can suppress it. It is also the only
 * confirmation surface in the app — `tests/unit/ui/confirmDialog.test.js` fails
 * the build if a `window.confirm` call site comes back.
 */
let Confirm = {};

(function () {
    'use strict';


    /** One dialog at a time; a second question replaces the first. */
    let active = null;

    /**
     * A MODAL QUESTION OWNS THE SCREEN, and it has to say so out loud.
     *
     * Reported from a phone: "tutorial text is rendered above return to lobby
     * confirmation text". Measured at the 844x288 page box in Chromium, Firefox
     * and WebKit, the dialog does paint on top — but the tutorial's coach mark
     * survives beside it as a gold-bordered, near-opaque panel of instructions,
     * so the player is handed two things to read at the moment one decision is
     * being asked for. Stacking was never the lever.
     *
     * The dialog cannot reach into the tutorial (or the next surface that
     * coaches) without knowing about it, so it announces itself on the document
     * instead and lets each surface decide what stepping aside means.
     */
    const OPEN_MARKER = 'mf-confirm-open';

    function markDocument(open) {
        // A dialog can be built before <body> exists in a very early failure
        // path; a missing marker must never be what throws.
        const body = document.body;
        if (!body) return;
        body.classList.toggle(OPEN_MARKER, open);
    }

    const translate = (key, fallback) => (
        typeof window.uiText === 'function' ? window.uiText(key, fallback) : fallback
    );

    function teardown(result, { restoreFocus = true } = {}) {
        if (!active) return false;
        const {
            root,
            resolve,
            restoreFocus: returnFocus,
            onKeydown,
        } = active;
        active = null;
        markDocument(false);
        document.removeEventListener('keydown', onKeydown, true);
        root.remove();
        // Hand focus back to whatever opened the dialog, or the keyboard user
        // is dropped at the top of the document.
        if (restoreFocus && typeof returnFocus.focus === 'function' && returnFocus.isConnected) {
            try { returnFocus.focus(); } catch (_) { /* focus is best effort */ }
        }
        resolve(result);
        return true;
    }

    /**
     * Ask the player to confirm an action.
     *
     * @param {string} message  Plain text. Inserted with textContent — never markup.
     * @param {object} [options]
     * @param {string} [options.confirmLabel]  Accept-button text.
     * @param {string} [options.cancelLabel]   Cancel-button text.
     * @param {boolean} [options.destructive]  Style the accept button as a danger action.
     * @param {{label: string}} [options.requiredCheck]  An unticked box the
     *   player must tick before accepting — an express request that is ours to
     *   collect (the store's in-place pass upgrade). Accept stays disabled
     *   until it is ticked, so `true` always means it was.
     * @returns {Promise<boolean>} true if accepted, false for cancel/Escape/backdrop.
     */
    function ask(message, options) {
        const settings = options || {};
        // An unanswered question must never leave its caller awaiting forever.
        if (active) teardown(false);

        const root = document.createElement('div');
        root.className = 'mf-confirm';

        const panel = document.createElement('div');
        panel.className = 'mf-confirm-panel';
        panel.setAttribute('role', 'alertdialog');
        panel.setAttribute('aria-modal', 'true');

        const body = document.createElement('p');
        body.className = 'mf-confirm-message';
        body.id = `mf-confirm-message-${Date.now().toString(36)}`;
        body.textContent = String(message == null ? '' : message);
        panel.setAttribute('aria-describedby', body.id);

        /* An optional second, quieter paragraph: the rules that govern the
           answer. It belongs HERE rather than painted permanently onto the
           screen behind the button, because this is the moment the player is
           deciding — and a screen carrying its every rule as ambient body copy
           reads as clutter long before anyone needs it. */
        let detail = null;
        if (settings.detail) {
            detail = document.createElement('p');
            detail.className = 'mf-confirm-detail';
            detail.id = `${body.id}-detail`;
            detail.textContent = String(settings.detail);
            panel.setAttribute('aria-describedby', `${body.id} ${detail.id}`);
        }

        let requiredBox = null;
        let requiredRow = null;
        if (settings.requiredCheck && settings.requiredCheck.label) {
            requiredRow = document.createElement('label');
            requiredRow.className = 'mf-confirm-check';
            requiredBox = document.createElement('input');
            requiredBox.type = 'checkbox';
            requiredBox.className = 'mf-confirm-check-box';
            const text = document.createElement('span');
            text.textContent = String(settings.requiredCheck.label);
            requiredRow.append(requiredBox, text);
        }

        const actions = document.createElement('div');
        actions.className = 'mf-confirm-actions';

        const cancel = document.createElement('button');
        cancel.type = 'button';
        // The app's canonical button pair, not a dialog-only look.
        cancel.className = 'btn deck-btn deck-btn-secondary mf-confirm-cancel';
        cancel.textContent = settings.cancelLabel || translate('common.cancel', 'Cancel');

        const accept = document.createElement('button');
        accept.type = 'button';
        accept.className = `btn deck-btn deck-btn-primary mf-confirm-accept${settings.destructive ? ' mf-confirm-destructive' : ''}`;
        accept.textContent = settings.confirmLabel || translate('common.confirm', 'Confirm');

        if (requiredBox) {
            accept.disabled = true;
            requiredBox.addEventListener('change', () => { accept.disabled = !requiredBox.checked; });
        }
        cancel.addEventListener('click', () => teardown(false));
        accept.addEventListener('click', () => {
            if (requiredBox && !requiredBox.checked) return;
            teardown(true);
        });
        // A tap on the backdrop is a cancel, the same as anywhere else in the app.
        root.addEventListener('click', (event) => {
            if (event.target === root) teardown(false);
        });

        // Cancel reads first in the DOM so it takes initial focus: the safe
        // answer should be the one a stray Enter gives you.
        actions.append(cancel, accept);
        panel.append(body, ...(detail ? [detail] : []), ...(requiredRow ? [requiredRow] : []), actions);
        root.append(panel);
        document.body.appendChild(root);
        // After the append, so the replacement teardown at the top of `ask`
        // cannot leave the incoming question unmarked.
        markDocument(true);

        const onKeydown = (event) => {
            if (!active) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                teardown(false);
                return;
            }
            if (event.key !== 'Tab') return;
            // Trap focus inside: the two buttons, and the required box if any.
            const focusables = (requiredBox ? [requiredBox, cancel, accept] : [cancel, accept])
                .filter((node) => !node.disabled);
            const index = focusables.indexOf(document.activeElement);
            event.preventDefault();
            const step = event.shiftKey ? -1 : 1;
            const next = focusables[(Math.max(index, 0) + step + focusables.length) % focusables.length];
            next.focus();
        };
        document.addEventListener('keydown', onKeydown, true);

        return new Promise((resolve) => {
            active = {
                root,
                resolve,
                onKeydown,
                restoreFocus: document.activeElement,
            };
            try { cancel.focus(); } catch (_) { /* focus is best effort */ }
        });
    }

    function dismiss(options) {
        return teardown(false, options);
    }

    Confirm = { ask, dismiss };
}());

export { Confirm };
